import React, { useState } from 'react'
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  DialogContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { HighlightOff as HighlightOffIcon } from '@mui/icons-material/'
import {
  BLOOD_GROUPS,
  EMPLOYMENT_TYPES,
  FIELD_TYPES,
  GENDERS,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../../utility/constants'
import { adminTeamsService } from '../../../services/admin/teams'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { toast } from 'react-toastify'
import DateAndDateTime from '../../DataEntryPage/Fields/DateAndDateTime'
import moment from 'moment'

const defaultValue = {
  first_name: '',
  email: '',
  last_name: '',
  is_admin: false,
  company_id: null,
  password: '',

  /* new columns */
  employee_id: null,
  department_id: 0,
  designation: null,
  employment_type: null,
  primary_contact: null,
  office_contact: null,
  date_of_joining: null,
  gender: null,
  permanent_address: null,
  temporary_address: null,
  date_of_leaving: null,
  is_terminated: false,
  blood_group: null,
  aadhar_number: null,
  pan_number: null,
  short_bio: '',
  employment_history: {},
  educational_background: {},
  reporting_manager_id: null,
  department_list_data_id: null,
  designation_id: null,
}

const AddEditUserDialog = ({
  users,
  departments,
  designations,
  isOpen,
  setOpenClose,
  fetchFreshList = () => {},
}) => {
  const { user } = useAuthContext()
  const [userDetails, setUserDetails] = useState({})
  const [selectedUser, setSelectedUser] = useState(null)

  const addEditUser = async () => {
    let payload = {
      ...userDetails,
      company_id: selectedUser?.company_id || user.company_id,
      password: '',
    }

    if (selectedUser == null) {
      payload.user_id = -1
    } else {
      payload.user_id = selectedUser.id
    }

    try {
      await adminTeamsService.addEditUser(user, payload)
      toast.success('Saved successfully!', TOAST_SETTINGS)
      setOpenClose(false)
      fetchFreshList()
    } catch (error) {
      toast.error(error.response.data.message || 'Something went wrong!', TOAST_SETTINGS_ERROR)
    }
  }

  // First prepare options (optional, but cleaner)
  const employmentTypeOptions = Object.entries(EMPLOYMENT_TYPES).map(([key, value]) => ({
    label: key,
    value: value,
  }))

  // Prepare the options (cleaner)
  const genderOptions = Object.entries(GENDERS).map(([key, value]) => ({
    label: key,
    value: value,
  }))

  // Prepare options cleanly
  const bloodGroupOptions = Object.entries(BLOOD_GROUPS).map(([key, value]) => ({
    label: key,
    value: value,
  }))

  return (
    <Dialog open={isOpen} onClose={() => setOpenClose(false)}>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{selectedUser == null ? 'Create User' : 'Edit User'}</Typography>
          <IconButton align="right" onClick={() => setOpenClose(false)} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Autocomplete
          style={{ marginTop: 0 }}
          value={selectedUser}
          size="small"
          options={users}
          getOptionLabel={(it) => it.first_name + ' ' + it.last_name + '(' + it.email + ')'}
          renderInput={(params) => <TextField {...params} label="Select User To Edit" />}
          onChange={(_, v) => {
            if (v) {
              setSelectedUser(v)
              setUserDetails({
                ...defaultValue,
                ...v,
                password: '',
              })
            } else {
              setSelectedUser(v)
              setUserDetails(defaultValue)
            }
          }}
        />
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.first_name}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              first_name: e.target.value,
            })
          }
          placeholder="First Name"
          size="small"
          disabled={false}
        />
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.last_name}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              last_name: e.target.value,
            })
          }
          placeholder="Last Name"
          size="small"
        />
        <TextField
          style={{ marginTop: 16 }}
          type="email"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.email}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              email: e.target.value,
            })
          }
          placeholder="Email"
          size="small"
        />

        <Autocomplete
          style={{ marginTop: 16 }}
          value={users.find((it) => it.id == userDetails.reporting_manager_id) || null}
          size="small"
          options={users.filter((it) => !it.is_terminated)}
          getOptionLabel={(it) => it.first_name + ' ' + it.last_name + '(' + it.email + ')'}
          renderInput={(params) => <TextField {...params} label="Select Reporting Manager" />}
          onChange={(_, v) =>
            setUserDetails({
              ...userDetails,
              reporting_manager_id: v?.id || null,
            })
          }
        />

        <TextField
          style={{ marginTop: 16 }}
          type="number"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.employee_id}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              employee_id: Number(e.target.value),
            })
          }
          placeholder="Employee Id"
          size="small"
        />

        <Autocomplete
          key={userDetails.department_list_data_id || 'department_list_data_id'}
          options={departments}
          getOptionLabel={(option) => option.value || ''}
          value={
            departments.find((item) => item.id === userDetails.department_list_data_id) || null
          }
          onChange={(e, newValue) =>
            setUserDetails({
              ...userDetails,
              department_list_data_id: newValue ? newValue.id : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Department"
              placeholder="Select Designation"
              size="small"
              fullWidth
              sx={{ marginTop: 2 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          fullWidth
          disableClearable
        />

        <Autocomplete
          key={userDetails.designation_id || 'designation_id'}
          options={designations}
          getOptionLabel={(option) => option.designation || ''}
          value={designations.find((dept) => dept.id === userDetails.designation_id) || null}
          onChange={(e, newValue) =>
            setUserDetails({
              ...userDetails,
              designation_id: newValue ? newValue.id : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Designation"
              placeholder="Select Designation"
              size="small"
              fullWidth
              sx={{ marginTop: 2 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          fullWidth
          disableClearable
        />

        <Autocomplete
          key={userDetails.employment_type || 'employment_type'}
          options={employmentTypeOptions}
          getOptionLabel={(option) => option.label || ''}
          value={
            employmentTypeOptions.find((item) => item.value === userDetails.employment_type) || null
          }
          onChange={(e, newValue) =>
            setUserDetails({
              ...userDetails,
              employment_type: newValue ? newValue.value : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employment Type"
              placeholder="Select Employment Type"
              size="small"
              fullWidth
              sx={{ marginTop: 2 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          fullWidth
          disableClearable
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.primary_contact}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              primary_contact: e.target.value,
            })
          }
          placeholder="Primary Contact"
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          size="small"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.office_contact}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              office_contact: e.target.value,
            })
          }
          placeholder="Office Contact"
        />

        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Joining"
          value={moment(userDetails.date_of_joining) || null}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              date_of_joining: e?.format('YYYY-MM-DD'),
            })
          }
        />

        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Birth"
          value={moment(userDetails.date_of_birth) || null}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              date_of_birth: e?.format('YYYY-MM-DD'),
            })
          }
        />

        <Autocomplete
          key={userDetails.gender || 'gender'}
          options={genderOptions}
          getOptionLabel={(option) => option.label || ''}
          value={genderOptions.find((item) => item.value === userDetails.gender) || null}
          onChange={(e, newValue) =>
            setUserDetails({
              ...userDetails,
              gender: newValue ? newValue.value : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Gender"
              placeholder="Select Gender"
              size="small"
              fullWidth
              sx={{ marginTop: 2 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          fullWidth
          disableClearable
        />

        <Autocomplete
          key={userDetails.blood_group || 'blood_group'}
          options={bloodGroupOptions}
          getOptionLabel={(option) => option.label || ''}
          value={bloodGroupOptions.find((item) => item.value === userDetails.blood_group) || null}
          onChange={(e, newValue) =>
            setUserDetails({
              ...userDetails,
              blood_group: newValue ? newValue.value : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Blood Group"
              placeholder="Select Blood Group"
              size="small"
              fullWidth
              sx={{ marginTop: 2 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          fullWidth
          disableClearable
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          size="small"
          fullWidth
          label={'Permanent Address'}
          value={userDetails.permanent_address}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              permanent_address: e.target.value,
            })
          }
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          size="small"
          fullWidth
          label={'Temporary Address'}
          value={userDetails.temporary_address}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              temporary_address: e.target.value,
            })
          }
        />

        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Leaving"
          value={moment(userDetails.date_of_leaving) || null}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              date_of_leaving: e?.format('YYYY-MM-DD'),
            })
          }
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.aadhar_number}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              aadhar_number: e.target.value,
            })
          }
          placeholder="Aadhar Number"
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          fullWidth
          color="primary"
          variant="outlined"
          value={userDetails.pan_number}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              pan_number: e.target.value,
            })
          }
          placeholder="PAN Number"
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          type="text"
          fullWidth
          color="primary"
          variant="outlined"
          label={'Short Bio'}
          value={userDetails.short_bio || ''}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              short_bio: e.target.value,
            })
          }
          size="small"
          multiline
          minRows={2}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="10"
                checked={userDetails.is_admin || false}
                color="primary"
                variant="outlined"
                onChange={(e) =>
                  setUserDetails({
                    ...userDetails,
                    is_admin: e.target.checked,
                  })
                }
              />
            }
            label={<Typography variant="body2">Admin?</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="10"
                checked={userDetails.is_terminated || false}
                color="primary"
                variant="outlined"
                onChange={(e) =>
                  setUserDetails({
                    ...userDetails,
                    is_terminated: e.target.checked,
                  })
                }
              />
            }
            label={<Typography variant="body2">Terminated?</Typography>}
          />
          <br />
          <br />

          {selectedUser == null ? (
            <Button
              color="primary"
              variant="contained"
              type="button"
              size="small"
              onClick={addEditUser}
            >
              Create User
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              type="button"
              size="small"
              onClick={addEditUser}
            >
              Update User Details
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddEditUserDialog
